import React from 'react';
import { ToggleSection } from '@/components/form/ToggleSection';
import { ReminderChip } from './ReminderChip';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface RemindersSectionProps {
  localAutoSendReminders: boolean;
  reminderDays: number[];
  paymentTermsDays: number | null;
  validationKey: string;
  onToggleAutoSendReminders: (checked: boolean) => void;
  onUpdateReminderDay: (idx: number, newValue: number | null) => void;
  onValidationComplete: (idx: number, isValid: boolean, validatedValue: number | null, originalValue: number) => void;
  onRemoveReminderDay: (index: number) => void;
  onAddEmptyReminder: () => void;
  onClearGroupError: (key: string) => void;
}

export const RemindersSection: React.FC<RemindersSectionProps> = ({
  localAutoSendReminders,
  reminderDays,
  paymentTermsDays,
  validationKey,
  onToggleAutoSendReminders,
  onUpdateReminderDay,
  onValidationComplete,
  onRemoveReminderDay,
  onAddEmptyReminder,
  onClearGroupError,
}) => {
  const handleToggleChange = (checked: boolean) => {
    onToggleAutoSendReminders(checked);
    if (!checked) onClearGroupError(validationKey);
  };

  return (
    <ToggleSection
      id="autoSendReminders"
      label="Auto-Send Reminders"
      description="Automatically remind clients of upcoming/overdue invoices"
      checked={localAutoSendReminders}
      onCheckedChange={handleToggleChange}
      variant="indented"
    >
      <div className="space-y-1">
        <Label className="text-base text-foreground">Reminder Days&nbsp;(before due date)</Label>
        <p className="text-xs text-muted-foreground">
          Add days before the due date to send reminders. Must be less than payment terms.
        </p>
      </div>
      <div 
        className="flex flex-wrap items-center gap-1 sm:gap-2"
        data-validation-key={validationKey}
      >
        {reminderDays.map((day, idx) => {
          const key = `reminder-chip-${idx}-${day}`;

          return (
            <ReminderChip
              key={key}
              index={idx}
              dayInState={day}
              allReminderDays={reminderDays}
              paymentTermsDays={paymentTermsDays}
              onUpdate={onUpdateReminderDay}
              onValidationComplete={onValidationComplete}
              onRemove={onRemoveReminderDay}
            />
          );
        })}

        <Button
          variant="secondary"
          size="sm"
          onClick={onAddEmptyReminder}
          className="self-start" 
        >
          +
        </Button>
      </div>
    </ToggleSection>
  );
};
